import type {
  BlogPostDto,
  BlogPostSummaryDto,
  CityAdminDto,
  CountryDto,
  EducationLevelAdminDto,
  EducationLevelDto,
  JobOptionKind,
  JobOptionsDto,
  BlogCategoryDto,
  JobRoleAdminDto,
  JobRoleDto,
  LandingCityDto,
  StateDto,
  DummyCompanyDto,
  PageMeta,
  PublicCompanyDetailDto,
  PublicCompanyDto,
  ReferralRewardDto,
  ReferralSummaryDto,
  SeoLandingDto,
  SeoMetaDto,
  SeoPageDto,
  ShowcaseDto,
  SupportTicketDto,
  TicketStatus,
} from '@talentbabu/types';
import type {
  BlogPostInput,
  CityInput,
  ContactFormInput,
  CountryInput,
  CreateTicketInput,
  DummyCompanyInput,
  EducationLevelInput,
  BlogCategoryInput,
  JobRoleInput,
} from '@talentbabu/validation';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, http } from '../../lib/api';

function useContentMutation<T, R>(fn: (input: T) => Promise<R>, key: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => void qc.invalidateQueries({ queryKey: [key] }) });
}

/* --------------------------------- SEO meta -------------------------------- */

export function useSeoMeta(path: string) {
  return useQuery({
    queryKey: ['seo-meta', path],
    queryFn: async () => (await apiGet<SeoMetaDto>('/seo/meta', { params: { path } })).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLandingPage(slug: string | undefined) {
  return useQuery({
    queryKey: ['seo', 'landing', slug],
    queryFn: async () => (await apiGet<SeoLandingDto>(`/seo/pages/${slug}`)).data,
    enabled: !!slug,
    retry: false,
  });
}

export function useSeoPages(params: { page: number; q?: string; type?: string }) {
  return useQuery({
    queryKey: ['seo', 'pages', params],
    queryFn: async () => {
      const res = await apiGet<SeoPageDto[]>('/seo/admin/pages', { params: { ...params, limit: 25 } });
      return { items: res.data, meta: res.meta as PageMeta };
    },
    placeholderData: keepPreviousData,
  });
}

export const useUpdateSeoPage = () =>
  useContentMutation(
    async ({
      id,
      input,
    }: {
      id: string;
      input: Pick<SeoPageDto, 'title' | 'h1' | 'metaDescription' | 'intro' | 'isActive'>;
    }) => (await apiPut<SeoPageDto>(`/seo/admin/pages/${id}`, input)).data,
    'seo',
  );

export const useGenerateSeo = () =>
  useContentMutation(
    async () => (await apiPost<{ upserted: number; total: number }>('/seo/admin/generate')).data,
    'seo',
  );

/* ---------------------------------- Blog ---------------------------------- */

export function useBlogPosts(params: { page: number; category?: string; tag?: string; q?: string }) {
  return useQuery({
    queryKey: ['blog', 'public', params],
    queryFn: async () => {
      const res = await apiGet<BlogPostSummaryDto[]>('/blog', { params: { ...params, limit: 12 } });
      return { items: res.data, meta: res.meta as PageMeta };
    },
    placeholderData: keepPreviousData,
  });
}

export function useBlogPost(slug: string | undefined) {
  return useQuery({
    queryKey: ['blog', 'post', slug],
    queryFn: async () => (await apiGet<BlogPostDto & { related: BlogPostSummaryDto[] }>(`/blog/${slug}`)).data,
    enabled: !!slug,
    retry: false,
  });
}

export function useAdminPosts(params: { page: number; status?: string; q?: string }) {
  return useQuery({
    queryKey: ['blog', 'admin', params],
    queryFn: async () => {
      const res = await apiGet<BlogPostSummaryDto[]>('/blog/admin/posts', { params: { ...params, limit: 20 } });
      return { items: res.data, meta: res.meta as PageMeta };
    },
    placeholderData: keepPreviousData,
  });
}

export function useAdminPost(id: string | undefined) {
  return useQuery({
    queryKey: ['blog', 'admin', 'one', id],
    queryFn: async () => (await apiGet<BlogPostDto>(`/blog/admin/posts/${id}`)).data,
    enabled: !!id,
  });
}

export const useSavePost = () =>
  useContentMutation(
    async ({ id, input }: { id?: string; input: BlogPostInput }) =>
      id
        ? (await apiPut<BlogPostDto>(`/blog/admin/posts/${id}`, input)).data
        : (await apiPost<BlogPostDto>('/blog/admin/posts', input)).data,
    'blog',
  );

/** Uploads an article cover image against a saved article. */
export const useUploadPostCover = () =>
  useContentMutation(async ({ id, file }: { id: string; file: File }) => {
    const form = new FormData();
    form.append('file', file);
    return (await apiPost<BlogPostDto>(`/blog/admin/posts/${id}/cover`, form)).data;
  }, 'blog');

export const usePostStatus = () =>
  useContentMutation(
    async ({ id, status }: { id: string; status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' }) =>
      (await apiPost<BlogPostDto>(`/blog/admin/posts/${id}/status`, { status })).data,
    'blog',
  );

export const useDeletePost = () =>
  useContentMutation(async (id: string) => http.delete(`/blog/admin/posts/${id}`), 'blog');

/* -------------------------------- Companies -------------------------------- */

export function usePublicCompanies(params: { page: number; q?: string; city?: string }) {
  return useQuery({
    queryKey: ['public', 'companies', params],
    queryFn: async () => {
      const res = await apiGet<PublicCompanyDto[]>('/public/companies', { params: { ...params, limit: 18 } });
      return { items: res.data, meta: res.meta as PageMeta };
    },
    placeholderData: keepPreviousData,
  });
}

export function usePublicCompany(slug: string | undefined) {
  return useQuery({
    queryKey: ['public', 'company', slug],
    queryFn: async () => (await apiGet<PublicCompanyDetailDto>(`/public/companies/${slug}`)).data,
    enabled: !!slug,
    retry: false,
  });
}

export const useContactForm = () =>
  useMutation({
    mutationFn: async (input: ContactFormInput) =>
      (await apiPost<{ reference: string }>('/public/contact', input)).data,
  });

/* -------------------------------- Referrals -------------------------------- */

export function useMyReferrals() {
  return useQuery({
    queryKey: ['referrals', 'me'],
    queryFn: async () => (await apiGet<ReferralSummaryDto>('/referrals/me')).data,
  });
}

export function useRewards(params: { page: number; status?: string }) {
  return useQuery({
    queryKey: ['referrals', 'rewards', params],
    queryFn: async () => {
      const res = await apiGet<ReferralRewardDto[]>('/referrals/rewards', { params: { ...params, limit: 20 } });
      return { items: res.data, meta: res.meta as PageMeta };
    },
    placeholderData: keepPreviousData,
  });
}

export const useDecideReward = () =>
  useContentMutation(
    async ({ id, status, note }: { id: string; status: 'APPROVED' | 'PAID' | 'CANCELLED'; note?: string }) =>
      (await apiPost<ReferralRewardDto>(`/referrals/rewards/${id}`, { status, ...(note ? { note } : {}) })).data,
    'referrals',
  );

/* --------------------------------- Support --------------------------------- */

export function useTickets(params: { page: number; status?: string; mine?: boolean; q?: string }) {
  return useQuery({
    queryKey: ['support', 'tickets', params],
    queryFn: async () => {
      const res = await apiGet<SupportTicketDto[]>('/support/tickets', {
        params: { ...params, mine: params.mine ? 'true' : undefined, limit: 20 },
      });
      return { items: res.data, meta: res.meta as PageMeta };
    },
    placeholderData: keepPreviousData,
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['support', 'ticket', id],
    queryFn: async () => (await apiGet<SupportTicketDto>(`/support/tickets/${id}`)).data,
    enabled: !!id,
  });
}

export function useTicketCounts(enabled = true) {
  return useQuery({
    queryKey: ['support', 'counts'],
    queryFn: async () => (await apiGet<Record<TicketStatus, number>>('/support/counts')).data,
    enabled,
  });
}

export const useCreateTicket = () =>
  useContentMutation(
    async (input: CreateTicketInput) => (await apiPost<SupportTicketDto>('/support/tickets', input)).data,
    'support',
  );

export const useReplyTicket = () =>
  useContentMutation(
    async ({ id, body, internal }: { id: string; body: string; internal?: boolean }) =>
      (await apiPost<SupportTicketDto>(`/support/tickets/${id}/messages`, { body, internal: !!internal })).data,
    'support',
  );

export const useUpdateTicket = () =>
  useContentMutation(
    async ({ id, ...input }: { id: string; status?: string; priority?: string; assigneeId?: string | null }) =>
      (await apiPatch<SupportTicketDto>(`/support/tickets/${id}`, input)).data,
    'support',
  );

/* ---------------------------- Company showcase ---------------------------- */

/** Landing carousel / all-companies page. The server decides between dummy and real data. */
export function useShowcase(params: { page: number; limit: number }) {
  return useQuery({
    queryKey: ['public', 'showcase', params],
    queryFn: async () => {
      const res = await apiGet<ShowcaseDto>('/public/showcase-companies', { params });
      return { ...res.data, meta: res.meta as PageMeta };
    },
    placeholderData: keepPreviousData,
  });
}

export function useShowcaseMode() {
  return useQuery({
    queryKey: ['showcase-admin', 'mode'],
    queryFn: async () => (await apiGet<{ useDummy: boolean }>('/content/showcase/mode')).data,
  });
}

export function useDummyCompanies() {
  return useQuery({
    queryKey: ['showcase-admin', 'dummy'],
    queryFn: async () => (await apiGet<DummyCompanyDto[]>('/content/showcase/dummy-companies')).data,
  });
}

function useShowcaseMutation<T, R>(fn: (input: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['showcase-admin'] });
      void qc.invalidateQueries({ queryKey: ['public', 'showcase'] });
    },
  });
}

export const useSetShowcaseMode = () =>
  useShowcaseMutation(
    async (useDummy: boolean) => (await apiPut<{ useDummy: boolean }>('/content/showcase/mode', { useDummy })).data,
  );

/** Creates or edits a dummy company, then uploads the chosen logo (if any) against the saved record. */
export const useSaveDummyCompany = () =>
  useShowcaseMutation(async ({ id, input, logo }: { id?: string; input: DummyCompanyInput; logo?: File }) => {
    const saved = id
      ? (await apiPut<DummyCompanyDto>(`/content/showcase/dummy-companies/${id}`, input)).data
      : (await apiPost<DummyCompanyDto>('/content/showcase/dummy-companies', input)).data;
    if (!logo) return saved;
    const form = new FormData();
    form.append('file', logo);
    return (await apiPost<DummyCompanyDto>(`/content/showcase/dummy-companies/${saved.id}/logo`, form)).data;
  });

export const useDeleteDummyCompany = () =>
  useShowcaseMutation(async (id: string) => apiDelete(`/content/showcase/dummy-companies/${id}`));

/* -------------------------------- Job roles -------------------------------- */

/** Job roles for the homepage cards (`limit`), the all-roles page and the job form (no limit). Counts already apply the fallback. */
export function usePublicJobRoles(limit?: number) {
  return useQuery({
    queryKey: ['public', 'job-roles', limit ?? 'all'],
    queryFn: async () => {
      const res = await apiGet<JobRoleDto[]>('/public/job-roles', { params: limit ? { limit } : {} });
      return { items: res.data, total: (res.meta as { total?: number } | undefined)?.total ?? res.data.length };
    },
    staleTime: 60_000,
  });
}

export function useAdminJobRoles() {
  return useQuery({
    queryKey: ['job-roles-admin'],
    queryFn: async () => (await apiGet<JobRoleAdminDto[]>('/content/job-roles')).data,
  });
}

function useJobRoleMutation<T, R>(fn: (input: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['job-roles-admin'] });
      void qc.invalidateQueries({ queryKey: ['public', 'job-roles'] });
    },
  });
}

/** Creates or edits a job role, then uploads the chosen image (if any) against the saved record. */
export const useSaveJobRole = () =>
  useJobRoleMutation(async ({ id, input, image }: { id?: string; input: JobRoleInput; image?: File }) => {
    const saved = id
      ? (await apiPut<JobRoleAdminDto>(`/content/job-roles/${id}`, input)).data
      : (await apiPost<JobRoleAdminDto>('/content/job-roles', input)).data;
    if (!image) return saved;
    const form = new FormData();
    form.append('file', image);
    return (await apiPost<JobRoleAdminDto>(`/content/job-roles/${saved.id}/image`, form)).data;
  });

export const useDeleteJobRole = () => useJobRoleMutation(async (id: string) => apiDelete(`/content/job-roles/${id}`));

/* ----------------------------- Blog categories ----------------------------- */

/** Categories for the public blog filters and the blog form. */
export function useBlogCategories() {
  return useQuery({
    queryKey: ['blog-categories', 'public'],
    queryFn: async () => (await apiGet<BlogCategoryDto[]>('/public/blog-categories')).data,
    staleTime: 60_000,
  });
}

export function useAdminBlogCategories() {
  return useQuery({
    queryKey: ['blog-categories', 'admin'],
    queryFn: async () => (await apiGet<BlogCategoryDto[]>('/content/blog-categories')).data,
  });
}

function useBlogCategoryMutation<T, R>(fn: (input: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blog-categories'] });
      void qc.invalidateQueries({ queryKey: ['blog'] });
    },
  });
}

export const useSaveBlogCategory = () =>
  useBlogCategoryMutation(async ({ id, input }: { id?: string; input: BlogCategoryInput }) =>
    id
      ? (await apiPut<BlogCategoryDto>(`/content/blog-categories/${id}`, input)).data
      : (await apiPost<BlogCategoryDto>('/content/blog-categories', input)).data,
  );

export const useDeleteBlogCategory = () =>
  useBlogCategoryMutation(async (id: string) => apiDelete(`/content/blog-categories/${id}`));

/* ----------------------------- Education levels ---------------------------- */

/** Education levels for the homepage slider and the job form. Counts already apply the fallback. */
export function usePublicEducationLevels() {
  return useQuery({
    queryKey: ['public', 'education-levels'],
    queryFn: async () => (await apiGet<EducationLevelDto[]>('/public/education-levels')).data,
    staleTime: 60_000,
  });
}

export function useAdminEducationLevels() {
  return useQuery({
    queryKey: ['education-levels-admin'],
    queryFn: async () => (await apiGet<EducationLevelAdminDto[]>('/content/education-levels')).data,
  });
}

function useEducationLevelMutation<T, R>(fn: (input: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['education-levels-admin'] });
      void qc.invalidateQueries({ queryKey: ['public', 'education-levels'] });
    },
  });
}

/** Creates or edits an education level, then uploads the chosen image (if any) against the saved record. */
export const useSaveEducationLevel = () =>
  useEducationLevelMutation(async ({ id, input, image }: { id?: string; input: EducationLevelInput; image?: File }) => {
    const saved = id
      ? (await apiPut<EducationLevelAdminDto>(`/content/education-levels/${id}`, input)).data
      : (await apiPost<EducationLevelAdminDto>('/content/education-levels', input)).data;
    if (!image) return saved;
    const form = new FormData();
    form.append('file', image);
    return (await apiPost<EducationLevelAdminDto>(`/content/education-levels/${saved.id}/image`, form)).data;
  });

export const useDeleteEducationLevel = () =>
  useEducationLevelMutation(async (id: string) => apiDelete(`/content/education-levels/${id}`));

/* ------------------------- Job type / work mode options ------------------------- */

/** Options of the Job Type and Work Mode dropdowns (job form, search filters, homepage slider). */
export function usePublicJobOptions() {
  return useQuery({
    queryKey: ['public', 'job-options'],
    queryFn: async () => (await apiGet<JobOptionsDto>('/public/job-options')).data,
    staleTime: 60_000,
  });
}

export function useAdminJobOptions() {
  return useQuery({
    queryKey: ['job-options-admin'],
    queryFn: async () => (await apiGet<JobOptionsDto>('/content/job-options')).data,
  });
}

function useJobOptionMutation<T, R>(fn: (input: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['job-options-admin'] });
      void qc.invalidateQueries({ queryKey: ['public', 'job-options'] });
    },
  });
}

/** Adds an option, or renames one when `id` is given. */
export const useSaveJobOption = () =>
  useJobOptionMutation(async ({ id, kind, label }: { id?: string; kind: JobOptionKind; label: string }) =>
    id
      ? (await apiPut(`/content/job-options/${id}`, { label })).data
      : (await apiPost('/content/job-options', { kind, label })).data,
  );

/** Saves the homepage selection and order: ids of job types and work modes, mixed, in display order. */
export const useSetJobOptionsHomepage = () =>
  useJobOptionMutation(
    async (ids: string[]) => (await apiPut<JobOptionsDto>('/content/job-options/homepage', { ids })).data,
  );

export const useDeleteJobOption = () =>
  useJobOptionMutation(async (id: string) => apiDelete(`/content/job-options/${id}`));

/* -------------------------------- Locations -------------------------------- */

/** Cities chosen for the landing page slider (public). */
export function useLandingCities() {
  return useQuery({
    queryKey: ['public', 'landing-cities'],
    queryFn: async () => (await apiGet<LandingCityDto[]>('/public/landing-cities')).data,
    staleTime: 60_000,
  });
}

export function useCountries() {
  return useQuery({
    queryKey: ['locations-admin', 'countries'],
    queryFn: async () => (await apiGet<CountryDto[]>('/content/locations/countries')).data,
  });
}

export function useStates(country: string | undefined) {
  return useQuery({
    queryKey: ['locations-admin', 'states', country],
    queryFn: async () => (await apiGet<StateDto[]>('/content/locations/states', { params: { country } })).data,
    enabled: !!country,
  });
}

export function useCities(country: string | undefined, state: string | undefined) {
  return useQuery({
    queryKey: ['locations-admin', 'cities', country, state],
    queryFn: async () =>
      (await apiGet<CityAdminDto[]>('/content/locations/cities', { params: { country, state } })).data,
    enabled: !!country && !!state,
  });
}

export function useAdminLandingCities() {
  return useQuery({
    queryKey: ['locations-admin', 'landing'],
    queryFn: async () => (await apiGet<CityAdminDto[]>('/content/locations/landing')).data,
  });
}

/** Any location change refreshes the admin lists, the shared location master and the public landing slider. */
function useLocationMutation<T, R>(fn: (input: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['locations-admin'] });
      void qc.invalidateQueries({ queryKey: ['masters', 'locations'] });
      void qc.invalidateQueries({ queryKey: ['public', 'landing-cities'] });
    },
  });
}

export const useSaveCountry = () =>
  useLocationMutation(async ({ id, input }: { id?: string; input: CountryInput }) =>
    id
      ? (await apiPatch<CountryDto>(`/content/locations/countries/${id}`, input)).data
      : (await apiPost<CountryDto>('/content/locations/countries', input)).data,
  );

export const useDeleteCountry = () =>
  useLocationMutation(async (id: string) => apiDelete(`/content/locations/countries/${id}`));

export const useSaveState = () =>
  useLocationMutation(async ({ id, country, name }: { id?: string; country: string; name: string }) =>
    id
      ? (await apiPatch<StateDto>(`/content/locations/states/${id}`, { name })).data
      : (await apiPost<StateDto>('/content/locations/states', { country, name })).data,
  );

export const useDeleteState = () =>
  useLocationMutation(async (id: string) => apiDelete(`/content/locations/states/${id}`));

export const useSaveCity = () =>
  useLocationMutation(async ({ id, input }: { id?: string; input: CityInput }) =>
    id
      ? (await apiPut<CityAdminDto>(`/content/locations/cities/${id}`, input)).data
      : (await apiPost<CityAdminDto>('/content/locations/cities', input)).data,
  );

export const useDeleteCity = () =>
  useLocationMutation(async (id: string) => apiDelete(`/content/locations/cities/${id}`));

export const useSetCityLanding = () =>
  useLocationMutation(
    async ({ id, show }: { id: string; show: boolean }) =>
      (await apiPut<CityAdminDto>(`/content/locations/cities/${id}/landing`, { show })).data,
  );

export const useSetCityLandingJobCount = () =>
  useLocationMutation(
    async ({ id, count }: { id: string; count: number | null }) =>
      (await apiPut<CityAdminDto>(`/content/locations/cities/${id}/landing-job-count`, { count })).data,
  );

export const useUploadCityImage = () =>
  useLocationMutation(async ({ id, file }: { id: string; file: File }) => {
    const form = new FormData();
    form.append('file', file);
    return (await apiPost<CityAdminDto>(`/content/locations/cities/${id}/landing-image`, form)).data;
  });
